import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../api';
import { saveRequest } from '../ui/save-request';
import { useStaffSession, useScopePending } from '../ui/StaffSession';
import { PageHeader } from '../ui/PageHeader';
import { Loading, Empty, ErrorState } from '../ui/State';
import { Link } from '../ui/ScopedLink';
import { ResponsiveTable } from '../ui/ResponsiveTable';
import { formatDateOnly, formatDateTime } from '../dates';
import { Action, Select, Text, FactsForm, downloadConveyancing, type Row, type Save } from './conveyancing-forms';
import { ConveyancingCompletionAlpha } from './ConveyancingCompletionAlpha';
import { ConveyancingUpload } from './ConveyancingUpload';
import {ConveyancingRegister} from './ConveyancingRegister';
import { Choices } from './ConsultationForms';
import { ConveyancingPackForm, ConveyancingServiceForm, ConveyancingTemplateForm } from './ConveyancingWork';
export function Conveyancing() {
  const session = useStaffSession(),
    pending = useScopePending(),
    building = session?.effectiveScope.kind === 'building' ? session.effectiveScope : undefined,
    { transactionId } = useParams();
  return building && !pending ? (
    <TransactionPage key={building.id + ':' + (transactionId ?? '')} building={building} id={transactionId} />
  ) : (
    <Empty>Choose a building to open conveyancing transactions.</Empty>
  );
}
function TransactionPage({ building, id }: { building: { id: string; name: string }; id?: string | undefined }) {
  const creationRef=useRef<HTMLDivElement>(null);
  const [setup, setSetup] = useState<Row>(),
    [data, setData] = useState<Row>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [refresh, setRefresh] = useState(0),
    [page, setPage] = useState(1),
    [stage, setStage] = useState(''),
    [search, setSearch] = useState('');
  useEffect(() => {
    let active = true;
    setError(undefined);
    Promise.all([
      api<Row>('/api/conveyancing/setup?building=' + building.id),
      api<Row>(
        id
          ? '/api/conveyancing/' + id
          : `/api/conveyancing?building=${building.id}&page=${page}&pageSize=18&q=${encodeURIComponent(search)}${stage ? '&stage=' + encodeURIComponent(stage) : ''}`
      )
    ])
      .then(([s, d]) => {
        if (active) {
          setSetup(s);
          setData(d);
        }
      })
      .catch((e) => active && setError(e));
    return () => {
      active = false;
    };
  }, [id, building.id, page, stage, search, refresh]);
  const save: Save = async (body, path) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await saveRequest<Row>(path ?? (id ? '/api/conveyancing/' + id + '/commands' : '/api/conveyancing'), { method: 'POST', body: JSON.stringify(body) });
      setMessage('Saved. Transaction records have been refreshed.');
      setRefresh((n) => n + 1);
      return r;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const download = async (path: string, name: string) => {
    try {
      await downloadConveyancing(path, name);
    } catch (e) {
      setError(e);
    }
  };
  if (!setup || !data) return error ? <ErrorState error={error} onRetry={() => setRefresh((n) => n + 1)} /> : <Loading />;
  const row = data.row,
    docs = setup.documents.filter((d: Row) => d.selectable),
    open = row && row.suite_version > 0 && !['Completed', 'Cancelled'].includes(row.transaction_stage),
    events = data.events ?? [],
    prepared = events.filter((e: Row) => e.event_type === 'Conveyancing suite pack prepared' && !e.detail.evidenceUnavailable).at(-1),
    issued = events.filter((e: Row) => e.event_type === 'Conveyancing suite pack issued' && e.detail.document);
  const command = (body: Row) => save({ ...body, version: row.suite_version }),
    enquiries = events.filter((e: Row) => e.event_type === 'Conveyancing Enquiry');
  return (
    <div className="demand-workflow conveyancing-workspace">
      <PageHeader
        title={id ? row.title : 'Conveyancing'}
        scope={{ kind: 'building', name: building.name }}
        context="One transaction case for enquiries, packs, consents, completion and direct business billing."
        primaryAction={!id&&setup.permissions.manage?{kind:'button',label:'Start transaction',icon:'plus',disabled:busy||!setup.properties.length||!setup.users.length,onClick:()=>{
          const details=creationRef.current?.querySelector('details');if(details){details.open=true;details.querySelector<HTMLElement>('select,input')?.focus();details.scrollIntoView({block:'start'});}
        }}:undefined}
      />
      {Boolean(error) && <ErrorState error={error} />} {message && <p role="status">{message}</p>}
      {!id ? (
        <>
          <div className="register-toolbar">
          <label>
            Find a transaction
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label>
            Transaction stage
            <select
              value={stage}
              onChange={(e) => {
                setStage(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All stages</option>
              {['Pre-sale', 'Pre-completion', 'Completion', 'Post-completion', 'Completed', 'Cancelled'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          </div>
          {data.items.length>0&&<ConveyancingRegister rows={data.items}/>}
          {!data.items.length&&<Empty title={search||stage?'No matching transactions':'No conveyancing transactions recorded'}
            body={search||stage?'Change the search or stage to find another recorded transaction.':'Start a transaction for a recorded property, select an active owner and retain the original enquiry and evidence. Preparing a case does not issue a pack or complete an ownership change.'}/>}
          {!id&&setup.permissions.manage&&(!setup.properties.length||!setup.users.length)&&<p role="status">{!setup.properties.length?'A recorded property is needed before a transaction can be started.':'An active transaction owner must be available before a transaction can be started.'} <Link to={'/buildings/'+building.id+'/mobilisation/premises'}>Review property register</Link></p>}
          {!setup.permissions.manage&&<p>View only · an authorised colleague can start a transaction.</p>}
          <nav className="pagination" aria-label="Conveyancing pages"><span>{data.total} transactions</span>
          {(data.total>18||page>1)&&<><button className="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous transactions
          </button>
          <button className="secondary" disabled={page * 18 >= data.total} onClick={() => setPage((p) => p + 1)}>
            Next transactions
          </button></>}
          </nav>
          {setup.permissions.manage&&setup.properties.length>0&&setup.users.length>0&&<div ref={creationRef} className="conveyancing-creation"><FactsForm setup={setup} busy={busy} save={save}/></div>}
        </>
      ) : (
        <>
          <p>
            <Link to="/contacts/conveyancing">Back to transactions</Link> · <Link to={'/communications/threads/' + row.thread_id}>Open correspondence case</Link>
            {setup.permissions.finance && (
              <>
                {' '}
                · <Link to={'/finance/business-invoices?case=' + id}>Business invoices and payments</Link>
              </>
            )}
          </p>
          <section>
            <h2>Transaction progress</h2>
            <p>
              {row.case_number} · {row.transaction_type} · <strong>{row.transaction_stage}</strong> · Owner: {setup.users.find((u: Row) => u.id === row.assigned_to)?.display_name}
            </p>
            <p>
              Target: {formatDateOnly(row.resolution_due_at)} · Next response: {formatDateOnly(row.response_due_at)}
            </p>
            <p>
              {data.services.filter((s: Row) => ['Completed', 'Not required', 'Cancelled'].includes(s.status)).length} of {data.services.length} services resolved.
            </p>
            <p>{row.transaction_details.notes}</p>
          </section>
          <SourceSummary source={data.source} />
          {open && setup.permissions.manage && <ConveyancingUpload caseId={id!} building={building.id} onSaved={() => setRefresh((n) => n + 1)} />}
          {!row.suite_version && (
            <>
              <p>
                This existing case and its historic packs are retained. Integrating it does not treat the old manual fee status as a receipt or automatically rebill earlier work.
              </p>
              {setup.permissions.manage && <FactsForm setup={setup} initial={row} busy={busy} save={save} />}
            </>
          )}
          {open && setup.permissions.manage && <FactsForm setup={setup} initial={row} busy={busy} save={save} />}
          <section>
            <h2>Services and completion requirements</h2>
            {data.services.map((s: Row) => (
              <article key={s.id}>
                <h3>{s.title}</h3>
                <p>{s.status}</p>
                {s.details.review && (
                  <p>
                    {s.details.review.requirements} · {s.details.review.reviewNote}
                  </p>
                )}
                {open && setup.permissions.manage && !['Completed', 'Cancelled', 'Not required'].includes(s.status) && (
                  <ConveyancingServiceForm key={s.id + ':' + s.version} service={s} setup={setup} busy={busy} command={command} />
                )}
                {open && setup.permissions.manage && (
                  <ConveyancingUpload caseId={id!} building={building.id} serviceId={s.id} title={'Upload evidence for ' + s.title} onSaved={() => setRefresh((n) => n + 1)} />
                )}
              </article>
            ))}
          </section>
          {open && setup.permissions.manage && (
            <Action
              title="Add transaction service"
              busy={busy}
              onSave={(v, key) => command({ action: 'Add service', kind: v.kind, title: v.title, reason: v.reason, idempotencyKey: key })}
            >
              <Select name="kind" label="Service" options={setup.serviceKinds.map((s: string) => [s, s])} />
              <Text name="title" label="Work description" />
            </Action>
          )}
          {open && setup.permissions.approveBilling && (
            <>
              <Action
                title="Approve a fee waiver or included work"
                busy={busy}
                documents={docs}
                onSave={(v, key, form) =>
                  command({ action: 'Waive fee', serviceIds: form.getAll('serviceIds'), documentIds: form.getAll('documentIds'), reason: v.reason, idempotencyKey: key })
                }
              >
                <Choices name="serviceIds" title="Services waived or included" options={data.services.map((s: Row) => ({ id: s.id, title: s.title }))} />
                <p>Use retained charging authority. An old manual Paid status is not a receipt.</p>
              </Action>
              <Action
                title="Withdraw a fee waiver"
                busy={busy}
                documents={docs}
                onSave={(v, key, form) =>
                  command({ action: 'Withdraw fee waiver', waiverId: v.waiverId, documentIds: form.getAll('documentIds'), reason: v.reason, idempotencyKey: key })
                }
              >
                <Select
                  name="waiverId"
                  label="Fee waiver"
                  options={events
                    .filter(
                      (e: Row) =>
                        e.event_type === 'Conveyancing Waive fee' && !events.some((w: Row) => w.event_type === 'Conveyancing Withdraw fee waiver' && w.detail.waiverId === e.id)
                    )
                    .map((e: Row) => [e.id, e.detail.reason])}
                />
              </Action>
            </>
          )}
          <section>
            <h2>Management pack and enclosures</h2>
            <p>
              Review the recorded sources and answer every required question. Unknown information is explicitly disclosed. An official form must be retained as its own reviewed
              enclosure.
            </p>
            {open && setup.permissions.approve && <ConveyancingTemplateForm setup={setup} jurisdiction={row.transaction_details.jurisdiction} busy={busy} save={save} />}
            {open && setup.permissions.manage && <ConveyancingPackForm setup={setup} data={data} busy={busy} command={command} />}
            {open && setup.permissions.approve && prepared && (
              <Action
                title="Approve and issue the reviewed pack"
                busy={busy}
                onSave={(v, key) => command({ action: 'Issue pack', draftId: prepared.id, reason: v.reason, idempotencyKey: key })}
              >
                <p>
                  The latest pack draft must still match this transaction and current sources. The management-pack invoice must be issued and reconciled. Issuing retains a PDF in
                  Documents and does not send it.
                </p>
              </Action>
            )}
            {issued.map((e: Row) => (
              <p key={e.id}>
                Issued {formatDateTime(e.created_at)} · <Link to={'/documents/' + e.detail.document.id}>Open retained pack</Link> ·{' '}
                <Link
                  to={
                    '/communications/compose?' +
                    new URLSearchParams([
                      ['subject', row.case_number + ' management pack'],
                      ...[e.detail.document, ...e.detail.evidence].map((d: Row) => ['document', d.id])
                    ]).toString()
                  }
                >
                  Prepare email with pack and enclosures
                </Link>{' '}
                · <button onClick={() => download(`/api/conveyancing/${id}/packs/${e.id}/pdf`, row.case_number + '-pack.pdf')}>Download issued pack PDF</button>
              </p>
            ))}
          </section>
          <section>
            <h2>Solicitor enquiries</h2>
            {enquiries.map((e: Row) => {
              const answer = events.filter((a: Row) => a.event_type === 'Conveyancing Answer enquiry' && a.detail.enquiryId === e.id).at(-1);
              return (
                <article key={e.id}>
                  <h3>{e.detail.question}</h3>
                  <p>
                    Received {formatDateOnly(e.detail.receivedOn)} · Response due {formatDateOnly(e.detail.dueOn)}
                  </p>
                  {answer ? (
                    <p>{answer.detail.answer}</p>
                  ) : open && setup.permissions.manage ? (
                    <Action
                      title="Answer this enquiry"
                      busy={busy}
                      documents={docs}
                      onSave={(v, key, form) =>
                        command({ action: 'Answer enquiry', enquiryId: e.id, answer: v.answer, documentIds: form.getAll('documentIds'), reason: v.reason, idempotencyKey: key })
                      }
                    >
                      <Text name="answer" label="Reviewed answer" type="textarea" />
                    </Action>
                  ) : (
                    <p>Awaiting response</p>
                  )}
                </article>
              );
            })}
            {open && setup.permissions.manage && (
              <Action
                title="Add solicitor enquiry"
                busy={busy}
                documents={docs}
                onSave={(v, key, form) =>
                  command({
                    action: 'Enquiry',
                    question: v.question,
                    receivedOn: v.receivedOn,
                    dueOn: v.dueOn || null,
                    solicitorPersonId: row.transaction_details.solicitorPersonId,
                    solicitorOrganisationId: row.transaction_details.solicitorOrganisationId,
                    documentIds: form.getAll('documentIds'),
                    reason: v.reason,
                    idempotencyKey: key
                  })
                }
              >
                <Text name="question" label="Solicitor’s enquiry" type="textarea" />
                <Text name="receivedOn" label="Received date" dateOnly />
                <Text name="dueOn" label="Response due date" dateOnly optional />
              </Action>
            )}
          </section>
          {row.transaction_type === 'Sale' && open && <ConveyancingCompletionAlpha caseId={id!} apiRoot={'/api/conveyancing/' + id} />}
          {open && setup.permissions.approve && (
            <Action
              title="Update transaction stage"
              busy={busy}
              documents={docs}
              onSave={(v, key, form) => command({ action: 'Stage', stage: v.stage, documentIds: form.getAll('documentIds'), reason: v.reason, idempotencyKey: key })}
            >
              <Select name="stage" label="Next stage" options={['Pre-sale', 'Pre-completion', 'Completion', 'Post-completion', 'Completed', 'Cancelled'].map((s) => [s, s])} />
              <p>
                Final completion checks every service, unanswered enquiry and direct business invoice. Ownership and historical liabilities remain governed by their existing
                review.
              </p>
            </Action>
          )}
          <section>
            <h2>History and evidence</h2>
            {events.map((e: Row) => (
              <details key={e.id}>
                <summary>
                  {e.event_type} — {formatDateTime(e.created_at)} — {e.actor_name ?? 'Recorded staff member'}
                </summary>
                <p>{e.detail.reason ?? e.detail.reviewNote ?? ''}</p>
                {e.event_type === 'Conveyancing pack issued' && (
                  <button onClick={() => download('/api/conveyancing/' + id + '/legacy-packs/' + e.id + '/html', 'retained-conveyancing-pack.html')}>
                    Download retained legacy pack
                  </button>
                )}
                {e.detail.evidence?.map((d: Row) => (
                  <p key={d.version_id}>
                    <Link to={'/documents/' + d.id}>{d.title ?? 'Open supporting document'}</Link>
                  </p>
                ))}
              </details>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
function SourceSummary({ source }: { source: Row }) {
  return (
    <details>
      <summary>Review existing building information</summary>
      <p>Retained ownership: {source.ownership.map((i: Row) => i.official_owner_name + ' (' + i.status + ')').join('; ') || 'Not recorded'}</p>
      <p>
        <Link to="/finance/service-charges">Service-charge statements, accounts and budgets</Link> ·{' '}
        {source.finance
          ? `${source.finance.accounts.length} property accounts; ${source.finance.budgets.length} budget versions`
          : 'Finance information unavailable to this account'}
        . Property history does not determine the seller’s liability.
      </p>
      <p>
        <Link to="/insurance">Insurance policies and claims</Link> · {source.insurance?.length ?? 'Access unavailable'}
      </p>
      <p>
        <Link to="/compliance">Compliance and fire safety</Link> · {source.compliance?.length ?? 'Access unavailable'}
      </p>
      <p>
        <Link to="/work/all">Repairs and major works</Link> · {source.work?.length ?? 'Access unavailable'} · <Link to="/work/consultations">Section 20 consultations</Link> ·{' '}
        {source.consultations?.length ?? 'Access unavailable'}
      </p>
      <p>
        <Link to="/documents">Documents and original evidence</Link>. Missing information must be marked unknown and reviewed rather than inferred.
      </p>
    </details>
  );
}
